/**
 * Secure key/value storage adapter.
 *
 *   - native (Capacitor): @aparajita/capacitor-secure-storage — Android Keystore /
 *     iOS Keychain (whenUnlockedThisDeviceOnly, no iCloud sync). Legacy values in
 *     @capacitor/preferences are migrated once at startup and removed.
 *   - web: localStorage (unchanged behaviour for the web app).
 *
 * Why this exists: the native shell runs cross-site to the API
 * (capacitor://localhost). The httpOnly refresh cookie is SameSite=Strict, so it
 * is never sent on native requests. We therefore persist the refresh token here
 * and use the BODY refresh path (tokenManager.getRefreshToken()).
 *
 * The secure-storage API is async, but several callers (tokenManager.getRefreshToken)
 * need a synchronous read. We keep an in-memory mirror that is populated on write
 * and via loadStoredTokens() at app startup, so synchronous reads work on native.
 */
import { isNative } from './platform'
import { SecureStorage } from '@aparajita/capacitor-secure-storage'
import { configureNativeSecureStorage, nativeSecureSetString } from './secureStorageConfig'

const PREFERENCES_MIGRATION_KEYS = ['refresh_token']

let _Preferences = null
let _initPromise = null

// Synchronous mirror for hot-path reads (refresh token on 401). Populated on
// every write and by loadStoredTokens().
const _memCache = Object.create(null)

async function _ensurePrefsLoaded() {
  if (!isNative()) return
  if (_Preferences) return
  try {
    const mod = await import('@capacitor/preferences')
    _Preferences = mod.Preferences
  } catch (e) {
    console.warn('[SecureStorage] @capacitor/preferences unavailable:', e?.message || e)
  }
}

/**
 * Configure Keychain/Keystore and migrate any legacy Preferences values.
 * @returns {Promise<void>}
 */
async function _initNativeStorage() {
  if (!isNative()) return
  if (_initPromise) return _initPromise

  _initPromise = (async () => {
    await configureNativeSecureStorage()
    await _migrateFromPreferences()
  })()

  return _initPromise
}

async function _migrateFromPreferences() {
  await _ensurePrefsLoaded()
  const P = _Preferences
  if (!P) return

  for (const key of PREFERENCES_MIGRATION_KEYS) {
    try {
      const { value } = await P.get({ key })
      if (value == null || value === '') continue

      const existing = await SecureStorage.get(key)
      if (existing == null) {
        await nativeSecureSetString(key, String(value))
      }
      await P.remove({ key })
    } catch (e) {
      console.warn('[SecureStorage] migration failed for', key, e?.message || e)
    }
  }
}

/**
 * Persist a value. Resolves once written; never throws.
 * @param {string} key
 * @param {string} value
 */
export async function secureSet(key, value) {
  _memCache[key] = value
  if (isNative()) {
    try {
      await _initNativeStorage()
      await nativeSecureSetString(key, value)
    } catch (e) {
      console.warn('[SecureStorage] set failed:', e?.message || e)
    }
    return
  }
  try { localStorage.setItem(key, value) } catch { /* storage full / unavailable */ }
}

/**
 * Read a value. Returns null when absent. Never throws.
 * @param {string} key
 * @returns {Promise<string|null>}
 */
export async function secureGet(key) {
  if (key in _memCache) return _memCache[key]
  if (isNative()) {
    try {
      await _initNativeStorage()
      const raw = await SecureStorage.get(key)
      const value = raw == null ? null : String(raw)
      _memCache[key] = value
      return value
    } catch (e) {
      console.warn('[SecureStorage] get failed:', e?.message || e)
    }
    return null
  }
  try { return localStorage.getItem(key) } catch { return null }
}

/**
 * Remove a value. Never throws.
 * @param {string} key
 * @returns {Promise<boolean>} true when the key is gone (or was never stored)
 */
export async function secureRemove(key) {
  delete _memCache[key]
  if (isNative()) {
    try {
      await _initNativeStorage()
      const ok = await SecureStorage.remove(key)
      if (ok === false) {
        console.warn('[SecureStorage] remove returned false for', key)
      }
      return ok !== false
    } catch (e) {
      console.warn('[SecureStorage] remove failed:', e?.message || e)
      return false
    }
  }
  try {
    localStorage.removeItem(key)
    return true
  } catch {
    return false
  }
}

/**
 * Wipe every Keychain / Keystore entry for this app prefix and drop the in-memory
 * mirror. Used on the first launch after reinstall when individual remove() calls
 * can return false for legacy items written under different access attributes.
 * @returns {Promise<boolean>}
 */
export async function secureClearAllNative() {
  if (!isNative()) return false
  for (const k of Object.keys(_memCache)) delete _memCache[k]
  try {
    await _initNativeStorage()
    await SecureStorage.clear()
    return true
  } catch (e) {
    console.warn('[SecureStorage] clear all failed:', e?.message || e)
    return false
  }
}

/**
 * Synchronous read from the in-memory mirror. Returns null when the key has not
 * been written/preloaded in this session. Use loadStoredTokens() at startup so the
 * value is available before the first synchronous read.
 * @param {string} key
 * @returns {string|null}
 */
export function secureGetCached(key) {
  return key in _memCache ? _memCache[key] : null
}

/**
 * Warm the in-memory mirror for the given keys (call once at app startup on
 * native, before any synchronous read).
 * @param {string[]} keys
 */
export async function preloadSecure(keys = []) {
  if (!isNative()) return
  for (const k of keys) {
    try { await secureGet(k) } catch { /* ignore */ }
  }
}
